import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ZoomOut, RotateCcw, ChevronDown } from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

/**
 * Sample-invoice viewer with subtle champagne highlight on the invoice number.
 * Overlay coordinates are anchored to the invoice image so they scale/pan with zoom.
 * Coordinates were measured from the source PDF via PyMuPDF text search.
 */
const DEFAULT_SAMPLE =
  process.env.REACT_APP_SAMPLE_INVOICE_URL || "/sample-invoice-125.png";

// Bounding box (as % of image) around the "Invoice No." + "125" block.
// Source PDF measurements: Invoice No. label top=20.5%, "125" top=22.3%.
// Padding is added on all sides for an airy luxury feel.
const HIGHLIGHT = {
  top: "19.6%",
  left: "4.4%",
  width: "10.2%",
  height: "4.6%",
};

export const SampleInvoiceModal = ({ open, onClose, imageUrl }) => {
  const src = imageUrl || DEFAULT_SAMPLE;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-ink/95 flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Sample invoice — where to find your invoice number"
          data-testid="sample-invoice-modal"
        >
          {/* Header bar */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-ivory/60">A Little Guide</p>
              <p className="text-sm font-serif text-ivory mt-0.5">Where to find your invoice number</p>
            </div>
            <button
              data-testid="close-invoice-modal"
              onClick={onClose}
              className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-ivory flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white/40"
              aria-label="Close sample invoice"
              autoFocus
            >
              <X size={20} strokeWidth={1.6} />
            </button>
          </div>

          {/* Zoom / pan area */}
          <div className="flex-1 min-h-0 overflow-hidden relative">
            <TransformWrapper
              initialScale={1}
              minScale={0.8}
              maxScale={5}
              centerOnInit
              wheel={{ step: 0.1 }}
              pinch={{ step: 5 }}
              doubleClick={{ mode: "toggle", step: 1.2 }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <TransformComponent
                    wrapperStyle={{ width: "100%", height: "100%" }}
                    contentStyle={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {/*
                      Wrapper matches the intrinsic aspect ratio of the invoice.
                      The highlight + label sit inside so they scale with pinch-zoom.
                      Extra top padding leaves room for the floating "Your Invoice Number" guide card
                      above the invoice header — so nothing on the invoice itself is obscured.
                    */}
                    <div
                      className="relative"
                      style={{
                        width: "min(92vw, calc(62vh / 1.4151))",
                        paddingTop: "4.5rem",
                      }}
                    >
                      {/* Floating guide card above the invoice, horizontally aligned to the highlight */}
                      <div
                        className="absolute pointer-events-none"
                        style={{
                          top: 0,
                          left: `calc(${HIGHLIGHT.left} + ${HIGHLIGHT.width} / 2)`,
                          transform: "translateX(-50%)",
                        }}
                      >
                        <div className="flex flex-col items-center">
                          <div className="bg-ivory/95 backdrop-blur-sm rounded-md px-3 py-1.5 sm:px-3.5 sm:py-2 shadow-[0_10px_28px_-10px_rgba(0,0,0,0.45)] border border-[rgba(198,158,90,0.55)] text-center">
                            <p
                              className="font-serif italic text-ink leading-none whitespace-nowrap"
                              style={{ fontSize: "clamp(10px, 1.6vw, 13px)" }}
                            >
                              Your Invoice Number
                            </p>
                            <p
                              className="text-muted2 mt-1 leading-tight whitespace-nowrap"
                              style={{ fontSize: "clamp(8px, 1.15vw, 10px)" }}
                            >
                              Enter this number in the form above.
                            </p>
                          </div>
                          <ChevronDown
                            size={18}
                            strokeWidth={1.6}
                            className="mt-0.5"
                            style={{ color: "rgba(198, 158, 90, 0.9)" }}
                          />
                        </div>
                      </div>

                      <div className="relative shadow-[0_30px_90px_-30px_rgba(0,0,0,0.6)]">
                        <img
                          src={src}
                          alt="Sample Tanish Textiles invoice with the invoice number highlighted."
                          className="block w-full h-auto select-none"
                          draggable={false}
                          data-testid="sample-invoice-img"
                        />

                        {/* Subtle champagne highlight around the invoice-number area */}
                        <div
                          aria-hidden="true"
                          className="absolute pointer-events-none rounded-md"
                          style={{
                            top: HIGHLIGHT.top,
                            left: HIGHLIGHT.left,
                            width: HIGHLIGHT.width,
                            height: HIGHLIGHT.height,
                            border: "1.5px solid rgba(198, 158, 90, 0.95)",
                            boxShadow:
                              "0 0 0 3px rgba(198, 158, 90, 0.14), 0 8px 24px -10px rgba(198, 158, 90, 0.35)",
                          }}
                        />
                      </div>
                    </div>
                  </TransformComponent>

                  {/* Zoom controls */}
                  <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 backdrop-blur rounded-full px-2 py-1.5">
                    <button
                      onClick={() => zoomOut()}
                      data-testid="zoom-out-btn"
                      className="h-9 w-9 rounded-full text-ivory hover:bg-white/15 flex items-center justify-center transition-colors"
                      aria-label="Zoom out"
                    >
                      <ZoomOut size={18} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => resetTransform()}
                      data-testid="zoom-reset-btn"
                      className="h-9 w-9 rounded-full text-ivory hover:bg-white/15 flex items-center justify-center transition-colors"
                      aria-label="Reset zoom"
                    >
                      <RotateCcw size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => zoomIn()}
                      data-testid="zoom-in-btn"
                      className="h-9 w-9 rounded-full text-ivory hover:bg-white/15 flex items-center justify-center transition-colors"
                      aria-label="Zoom in"
                    >
                      <ZoomIn size={18} strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              )}
            </TransformWrapper>
          </div>

          {/* Footer hint */}
          <div className="px-5 py-3 border-t border-white/10 flex-shrink-0 text-center">
            <p className="text-[11px] uppercase tracking-[0.24em] text-ivory/60">
              Pinch or double-tap to zoom · Drag to pan
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default SampleInvoiceModal;
